import React, { useState } from "react";
import { Modal, Stack, Group, Radio, Text, Progress } from "@mantine/core";
import { Button } from "@/components/bnp-ui";
import { ExportFormat, ExportProgress } from "@/types/export";

interface ExportDialogProps {
  opened: boolean;
  onClose: () => void;
  onExport: (format: ExportFormat) => void;
  progress: ExportProgress;
  totalRecords: number;
  loading?: boolean;
}

const ExportDialog: React.FC<ExportDialogProps> = ({
  opened,
  onClose,
  onExport,
  progress,
  totalRecords,
  loading = false,
}) => {
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>("csv");

  const handleExport = () => {
    onExport(selectedFormat);
  };

  const handleClose = () => {
    if (progress.status === "processing" || progress.status === "preparing") {
      return;
    }
    onClose();
  };

  const isExporting =
    progress.status === "preparing" ||
    progress.status === "processing" ||
    progress.status === "downloading";

  const canExport = !isExporting && !loading;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Export Data"
      centered
      size="md"
      closeOnClickOutside={!isExporting}
      closeOnEscape={!isExporting}
    >
      <Stack gap="md">
        <div>
          <Text size="sm" fw={500} mb={8}>
            Select Export Format
          </Text>
          <Radio.Group
            value={selectedFormat}
            onChange={(value) => setSelectedFormat(value as ExportFormat)}
          >
            <Stack gap="xs">
              <Radio
                value="csv"
                label="CSV (Comma-separated values)"
                description="Compatible with Excel, Google Sheets, and other spreadsheet applications"
                disabled={isExporting}
              />
              <Radio
                value="excel"
                label="Excel (XLSX)"
                description="Microsoft Excel format with formatting support"
                disabled={isExporting}
              />
            </Stack>
          </Radio.Group>
        </div>

        <div
          style={{
            backgroundColor: "#e7f5ff",
            border: "1px solid #339af0",
            borderRadius: "4px",
            padding: "12px",
          }}
        >
          <Text size="sm" c="#1864ab">
            <strong>Total Records:</strong> {totalRecords.toLocaleString()}
            {totalRecords > 10000 && (
              <>
                <br />
                <Text size="xs" c="#495057" mt={4}>
                  Note: Export is limited to 10,000 records. Consider applying
                  filters to reduce the dataset.
                </Text>
              </>
            )}
          </Text>
        </div>

        {isExporting && (
          <div>
            <Text size="sm" fw={500} mb={8}>
              Export Progress
            </Text>
            <Progress
              value={progress.percentage}
              animated
              color={progress.status === "error" ? "red" : "blue"}
            />
            <Text size="xs" c="dimmed" mt={4}>
              {progress.message || "Processing..."}
            </Text>
          </div>
        )}

        {progress.status === "completed" && (
          <div
            style={{
              backgroundColor: "#d3f9d8",
              border: "1px solid #51cf66",
              borderRadius: "4px",
              padding: "12px",
            }}
          >
            <Text size="sm" c="#2b8a3e">
              <strong>✓ Export Completed</strong>
              <br />
              {progress.processedRecords} records exported successfully
            </Text>
          </div>
        )}

        {progress.status === "error" && progress.error && (
          <div
            style={{
              backgroundColor: "#f8d7da",
              border: "1px solid #f5c2c7",
              borderRadius: "4px",
              padding: "12px",
            }}
          >
            <Text size="sm" c="#842029">
              <strong>Error:</strong> {progress.error}
            </Text>
          </div>
        )}

        <Group justify="flex-end" mt="md">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isExporting}
          >
            {progress.status === "completed" ? "Close" : "Cancel"}
          </Button>
          <Button
            onClick={handleExport}
            disabled={!canExport}
            style={{
              backgroundColor: canExport ? "#228be6" : "#ced4da",
              color: "#fff",
            }}
          >
            {isExporting ? "Exporting..." : "Export"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ExportDialog;
